/**
 * Shared line item calculation logic.
 *
 * Correct flow:
 * 1. baseHT = priceHT × quantity
 * 2. reduction on HT: totalHT = baseHT - reduction
 * 3. vat on reduced HT: vat = totalHT × (vatPercent / 100)
 * 4. totalTTC = totalHT + vat
 */

export interface LineItemCalcInput {
  priceHT?: number;
  quantity: number;
  vatAmount?: number;
  reductionAmount?: number;
  reductionType?: 'percentage' | 'amount';
}

export interface LineItemCalcResult {
  totalHT: number;
  totalTTC: number;
}

export function calculateLineItem(input: LineItemCalcInput): LineItemCalcResult {
  const priceHT = Number(input.priceHT) || 0;
  const quantity = Number(input.quantity) || 0;
  const vatPercent = Number(input.vatAmount) || 0;
  const reductionValue = Number(input.reductionAmount) || 0;

  // 1. Base HT = price × quantity
  const baseHT = priceHT * quantity;

  // 2. Apply reduction on HT
  const reduction =
    input.reductionType === 'percentage'
      ? baseHT * (reductionValue / 100)
      : reductionValue;

  const totalHT = Math.max(0, baseHT - reduction);

  // 3. VAT on reduced HT
  const vat = totalHT * (vatPercent / 100);

  // 4. TTC
  const totalTTC = totalHT + vat;

  return {
    totalHT: Number(totalHT.toFixed(2)),
    totalTTC: Number(totalTTC.toFixed(2)),
  };
}

export function calculateFormTotals(lineItems: { totalHT: number; totalTTC: number }[]): {
  totalHT: number;
  totalTTC: number;
} {
  let totalHT = 0;
  let totalTTC = 0;

  for (const item of lineItems) {
    totalHT += item.totalHT || 0;
    totalTTC += item.totalTTC || 0;
  }

  return {
    totalHT: Number(totalHT.toFixed(2)),
    totalTTC: Number(totalTTC.toFixed(2)),
  };
}

export function calculateDisplayTotals(lineItems: (LineItemCalcInput & { totalHT?: number; totalTTC?: number })[]): {
  totalHT: number;
  totalRemise: number;
  totalHTFinal: number;
  totalVAT: number;
  totalTTC: number;
} {
  let totalHT = 0;
  let totalRemise = 0;
  let totalHTFinal = 0;
  let totalVAT = 0;
  let totalTTC = 0;

  for (const item of lineItems) {
    const priceHT = Number(item.priceHT) || 0;
    const quantity = Number(item.quantity) || 0;
    const vatPercent = Number(item.vatAmount) || 0;
    const reductionValue = Number(item.reductionAmount) || 0;

    // When quantity is 0 but stored totalHT/totalTTC exist (e.g. migrated items),
    // use the stored values directly instead of recomputing from priceHT × quantity.
    if (quantity === 0 && typeof item.totalHT === 'number' && item.totalHT > 0) {
      const storedHT = item.totalHT;
      const storedTTC = typeof item.totalTTC === 'number' ? item.totalTTC : storedHT;
      totalHT += storedHT;
      totalHTFinal += storedHT;
      totalVAT += storedTTC - storedHT;
      totalTTC += storedTTC;
      continue;
    }

    const baseHT = priceHT * quantity;

    const reduction =
      item.reductionType === 'percentage'
        ? baseHT * (reductionValue / 100)
        : reductionValue;

    const htAfterReduction = Math.max(0, baseHT - reduction);
    const vat = htAfterReduction * (vatPercent / 100);

    totalHT += baseHT;
    totalRemise += reduction;
    totalHTFinal += htAfterReduction;
    totalVAT += vat;
    totalTTC += htAfterReduction + vat;
  }

  return {
    totalHT: Number(totalHT.toFixed(2)),
    totalRemise: Number(totalRemise.toFixed(2)),
    totalHTFinal: Number(totalHTFinal.toFixed(2)),
    totalVAT: Number(totalVAT.toFixed(2)),
    totalTTC: Number(totalTTC.toFixed(2)),
  };
}
